// src/context/StoreSiteContext.tsx
import React, { createContext, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSiteData, fetchStoreData } from '../../redux/ordersSlice';
import { AppDispatch } from '../../redux/store';
import { Site, Store } from '../../services/types';

type GroupedOption = {
  label: string;
  options: { label: string; value: string }[];
};

interface StoreSiteContextValue {
  options: GroupedOption[];
  siteNames: Site[];
  storeNames: Store[];
  refresh: () => void;
}
interface RootState {
  loadOrders: {
    storeNames: Store[];
    siteNames: Site[];
  };
}
const StoreSiteContext = createContext<StoreSiteContextValue | undefined>(undefined);

export const StoreSiteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { storeNames, siteNames } = useSelector((state: RootState) => state.loadOrders);

  const [options, setOptions] = useState<GroupedOption[]>([]);

  // Ref tracks latest Redux values so the interval callback never reads stale closures
  const dataRef = useRef({ storeNames, siteNames });
  dataRef.current = { storeNames, siteNames };

  // Kick off fetch when token is available
  const refresh = () => {
    const token = localStorage.getItem('userToken');
    if (token) {
      dispatch(fetchStoreData());
      dispatch(fetchSiteData());
    }
  };

  useEffect(() => {
    // Track whether the initial mount already dispatched a fetch,
    // so the polling interval doesn't duplicate it before thunks resolve.
    const didFetchOnMount = !!localStorage.getItem('userToken');
    refresh();

    // Poll for token availability after login (storage events don't fire in same tab)
    const interval = setInterval(() => {
      if (localStorage.getItem('userToken') && (!dataRef.current.storeNames?.length || !dataRef.current.siteNames?.length)) {
        if (!didFetchOnMount) {
          refresh();
        }
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [dispatch]);
  function buildGroupedOptions(sites: Site[], stores: Store[]): GroupedOption[] {
    return sites?.map(site => ({
      label: site.name,
      options: stores?.filter(store => store.siteId === site.siteId)
        .map(store => ({
          value: store.storeId,
          label: store.storeName,
        })),
    })).filter(group => group.options.length > 0);
  }
  useEffect(() => {
    if (siteNames?.length && storeNames?.length) {
      const grouped = buildGroupedOptions(
        siteNames.map((site: Site) => ({ ...site, siteId: String(site.siteId) })),
        storeNames.map((store: Store) => ({
          ...store,
          storeId: String(store.storeId),
          siteId: String(store.siteId)
        }))
      );
      setOptions(grouped);
    }
  }, [siteNames, storeNames]);

  const value: StoreSiteContextValue = useMemo(() => ({
    options,
    siteNames,
    storeNames,
    refresh,
  }), [options, siteNames, storeNames, refresh]);

  return (
    <StoreSiteContext.Provider value={value}>
      {children}
    </StoreSiteContext.Provider>
  );
};

// Convenience hook
export const useStoreSite = (): StoreSiteContextValue => {
  const ctx = useContext(StoreSiteContext);
  if (!ctx) {
    throw new Error('useStoreSite must be used inside StoreSiteProvider');
  }
  return ctx;
};
